import Product from "./product";
import { useCartMutations } from "./hooks/useCartMutations";
import { CartResponse } from "@/networking/carts.api";
import { useTranslation } from "@/hooks/useTranslation";

const CURRENCY_RE = /[^\d.-]/g;
const toNumber = (v: string) => parseFloat(v.replace(CURRENCY_RE, ""));
const symbolOf = (v: string) => v.match(CURRENCY_RE)?.[0] || "$";

export default function ProductList({
  cart,
  cartToken,
  paymentMethodsIncludes,
  isSubscriptionPending = false,
  isQuantityPending = false,
}: {
  cart: CartResponse["cart"] | null;
  cartToken: string;
  paymentMethodsIncludes: string | null;
  isSubscriptionPending?: boolean;
  isQuantityPending?: boolean;
}) {
  const { t } = useTranslation();
  const { updateItemSubscription } = useCartMutations(
    cartToken,
    paymentMethodsIncludes,
  );

  const getSubscriptionDetails = (interval: number, unit: string) => {
    return interval === 1
      ? `${t("delivered_every")} ${unit}`
      : `${t("delivered_every")} ${interval} ${unit}`;
  };

  const enrollmentPackData = cart?.enrollment_pack
    ? {
        enrollment_pack_id: cart.enrollment_pack.enrollment_pack_id,
        title: cart.enrollment_pack.title,
        imageUrl: cart.enrollment_pack.images?.[0]?.image_url,
      }
    : null;

  const sortedItems = [...(cart?.items ?? [])].sort(
    (a, b) => Number(a.free_item) - Number(b.free_item),
  );

  return (
    <ul role="list" className="w-full flex-auto overflow-y-auto">
      {sortedItems.map((item, index) => {
        const subscriptionDetails = getSubscriptionDetails(
          item.subscription_interval || 0,
          item.subscription_interval_unit || "",
        );

        const currency =
          cart?.currency_symbol || symbolOf(item.price_in_currency);
        const base = toNumber(item.price) || 0;
        const discount = toNumber(item.subscribe_and_save || "0") || 0;
        const originalUnitPrice = Number((base + discount).toFixed(2));
        // Pre-formatted "Save 10%" / "Save $5.00" from the selected plan, if
        // the backend exposes it on the :cart_item view. Null when the cart
        // is on a backend without the field — Product falls back to its
        // legacy currency-only chip.
        const selectedPlanSavingsLabel =
          item.subscription_plans?.find(
            (p) => p.id === item.subscription_plan_id,
          )?.savings_label ?? null;
        const isEnrollmentItem =
          item.enrollment &&
          item.enrollment_pack_id === enrollmentPackData?.enrollment_pack_id;
        const imageForPreview =
          (isEnrollmentItem && enrollmentPackData?.imageUrl) ||
          item.variant?.primary_image ||
          item.variant?.image_url ||
          item.product?.image_url ||
          item.image_url ||
          "";
        const isFirstFreeItem =
          item.free_item && index === sortedItems.findIndex((i) => i.free_item);

        return (
          <Product
            key={item.id}
            productTitle={
              item.product?.title || item.product_title || item.title || ""
            }
            variantOptions={item.variant?.options || []}
            imageUrl={imageForPreview}
            displayPrice={Number(item.price)}
            originalPrice={originalUnitPrice}
            allowSubscription={item.allow_subscription && !item.free_item}
            subscriptionDetails={subscriptionDetails}
            quantity={item.quantity}
            variantId={item.variant?.id}
            cartToken={cartToken}
            currencySymbol={currency}
            isSubscribed={item.subscription}
            updateSubscription={(planId, sub) =>
              updateItemSubscription.mutate({
                itemId: item.id,
                subscription: sub ?? !item.subscription,
                subscriptionPlanId: planId,
              })
            }
            selectedSubscriptionPlanId={item.subscription_plan_id || undefined}
            subscribeAndSave={item.subscribe_and_save}
            savingsLabel={selectedPlanSavingsLabel}
            itemId={item.id}
            productId={item.product?.id}
            errors={item.errors}
            paymentMethodsIncludes={paymentMethodsIncludes ?? null}
            subscriptionPlans={item.subscription_plans ?? null}
            isLast={index === sortedItems.length - 1}
            isFree={item.free_item}
            displayPromotionsHeader={isFirstFreeItem}
            isBundle={item?.bundled_items && item?.bundled_items?.length > 0}
            bundleItems={item.bundled_items}
            isSubscriptionPending={isSubscriptionPending}
            isQuantityPending={isQuantityPending}
            enrollmentPackData={enrollmentPackData}
            enrollmentPackId={
              item.enrollment ? item.enrollment_pack_id : undefined
            }
            creditablePoints={item.creditable_points}
            rewardPointsEnabled={
              (cart?.points_enabled || cart?.company?.reward_points_enabled) ??
              false
            }
            rewardPointsLabelSingular={
              cart?.company?.reward_points_label_singular
            }
            rewardPointsLabelPlural={cart?.company?.reward_points_label_plural}
          />
        );
      })}
    </ul>
  );
}
